import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
  Signal,
  TemplateRef,
  viewChild,
  WritableSignal,
} from '@angular/core';
import {
  CustomerModel,
  EmployeeCreateModel,
  EmployeeModel,
  EmployeeRoleModel,
  EmployeeRoleRefModel,
  UserCustomerProcurationModel,
} from '@libs/account/models';
import {
  AlertComponent,
  ButtonComponent,
  ConfirmationDialogComponent,
  SideSheetDirective,
  SideSheetHeaderComponent,
} from '@libs/common/components';
import { AlertModel } from '@libs/common/models';
import { SelectionListValue } from '@libs/form/models';
import { AuthService } from '@libs/shared/auth';
import { DialogService } from '@libs/shared/common';
import { ConfirmationDialogData } from '@libs/shared/services';
import { filter, finalize, switchMap } from 'rxjs';
import { EmployeeService } from '../services';
import { EmployeeEditComponent } from './edit';
import { EmployeeInviteComponent } from './invite';
import { EmployeeListComponent } from './list';

interface EmployeeInviteValidationError {
  field?: string;
  error?: string;
  code?: string | null;
}

interface EmployeeInviteErrorContainer {
  data?: {
    exception?: {
      errors?: EmployeeInviteValidationError[];
    };
  };
}

@Component({
  selector: 'ui-account-employee',
  templateUrl: './employee.component.html',
  styleUrls: ['./employee.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    EmployeeListComponent,
    AlertComponent,
    ButtonComponent,
    SideSheetDirective,
    SideSheetHeaderComponent,
    EmployeeInviteComponent,
    EmployeeEditComponent,
  ],
})
export class EmployeeComponent implements OnInit {
  private readonly employeeService: EmployeeService = inject(EmployeeService);
  private readonly authService: AuthService = inject(AuthService);
  private readonly dialogService: DialogService = inject(DialogService);

  private readonly alertMessages: Record<string, AlertModel> = {
    inviteSuccess: {
      theme: 'success',
      message: 'Der Mitarbeiter wurde erfolgreich eingeladen.',
    },
    inviteAlreadyExists: {
      theme: 'error',
      message: 'Dieser Mitarbeiter ist bereits für diesen Kunden hinterlegt.',
    },
    inviteOwnEmail: {
      theme: 'error',
      message: 'Die eigene E-Mail-Adresse kann nicht für Einladungen verwendet werden.',
    },
    inviteError: {
      theme: 'error',
      message: 'Das hat leider nicht geklappt. Bitte versuchen Sie es erneut.',
    },
    renewInvitationSuccess: {
      theme: 'success',
      message: 'Die Einladung wurde erfolgreich erneut versendet.',
    },
    renewInvitationError: {
      theme: 'error',
      message: 'Die Einladung konnte nicht erneut versendet werden. Bitte versuchen Sie es erneut.',
    },
    editSuccess: {
      theme: 'success',
      message: 'Der Mitarbeiter wurde erfolgreich bearbeitet.',
    },
    editError: {
      theme: 'error',
      message: 'Der Mitarbeiter konnte nicht bearbeitet werden. Bitte versuchen Sie es erneut.',
    },
    deleteSuccess: {
      theme: 'success',
      message: 'Der Mitarbeiter wurde erfolgreich gelöscht.',
    },
    deleteError: {
      theme: 'error',
      message: 'Der Mitarbeiter konnte nicht gelöscht werden. Bitte versuchen Sie es erneut.',
    },
  };

  private readonly employeePendingDeletion: WritableSignal<EmployeeModel | undefined> = signal<EmployeeModel | undefined>(undefined);
  private readonly employeePendingEdit: WritableSignal<EmployeeModel | undefined> = signal<EmployeeModel | undefined>(undefined);

  public readonly employees: WritableSignal<EmployeeModel[] | undefined> = signal<EmployeeModel[] | undefined>(undefined);
  public readonly delegatableRoles: WritableSignal<EmployeeRoleModel[] | undefined> = signal<EmployeeRoleModel[] | undefined>(undefined);
  public readonly alertMessage: WritableSignal<AlertModel | undefined> = signal<AlertModel | undefined>(undefined);
  public readonly editingEmployee: Signal<EmployeeModel | undefined> = computed(() => this.employeePendingEdit());
  public readonly deleteConfirmationContentRef: Signal<TemplateRef<unknown> | undefined> = viewChild('deleteConfirmationContent', { read: TemplateRef<unknown> });

  public readonly currentCustomer: Signal<UserCustomerProcurationModel | undefined> = computed(() => {
    const customer: CustomerModel | undefined = this.authService.currentCustomer();

    if (!customer) {
      return undefined;
    }

    return {
      customerNumber: customer.id,
      alias: customer.alias ?? '',
      yaci: customer.yaci,
      mainUser: customer.mainUser,
      userPermissions: [],
    };
  });

  public ngOnInit(): void {
    this.loadEmployees();
    this.loadDelegatableRoles();
  }

  public onInviteSubmit(
    value: { email: string; assignableRoles: SelectionListValue },
    side: SideSheetDirective,
  ): void {
    const roles: EmployeeRoleRefModel[] = this.mapSelectionListValueToRoles(value.assignableRoles);

    const payload: EmployeeCreateModel = { email: value.email, roles };

    this.alertMessage.set(undefined);
    this.employeeService
      .createEmployee(payload)
      .pipe(finalize((): void => {
        side.close();
      }))
      .subscribe({
        next: () => {
          this.loadEmployees();
          this.alertMessage.set(this.alertMessages['inviteSuccess']);
        },
        error: (error: unknown) => {
          this.alertMessage.set(this.resolveInviteErrorMessage(error));
        },
      });
  }

  public onEditClicked(employee: EmployeeModel, side: SideSheetDirective): void {
    this.alertMessage.set(undefined);
    this.employeePendingEdit.set(employee);
    side.open();
  }

  public onRenewInvitationClicked(employee: EmployeeModel): void {
    this.alertMessage.set(undefined);
    this.employeeService.renewInvitationEmployee(employee.id).subscribe({
      next: () => {
        this.loadEmployees();
        this.alertMessage.set(this.alertMessages['renewInvitationSuccess']);
      },
      error: () => {
        this.alertMessage.set(this.alertMessages['renewInvitationError']);
      },
    });
  }

  public onEditSubmit(
    value: { employee: EmployeeModel | undefined; assignedRoles: SelectionListValue },
    side: SideSheetDirective,
  ): void {
    const employee: EmployeeModel | undefined = value.employee;

    if (!employee) {
      return;
    }

    this.alertMessage.set(undefined);
    this.employeeService
      .replaceEmployeeRoles(employee.id, this.mapSelectionListValueToRoles(value.assignedRoles))
      .pipe(finalize((): void => {
        this.employeePendingEdit.set(undefined);
        side.close();
      }))
      .subscribe({
        next: () => {
          this.loadEmployees();
          this.alertMessage.set(this.alertMessages['editSuccess']);
        },
        error: () => {
          this.alertMessage.set(this.alertMessages['editError']);
        },
      });
  }

  public onEditCancel(side: SideSheetDirective): void {
    this.employeePendingEdit.set(undefined);
    side.close();
  }

  public onDeleteClicked(employee: EmployeeModel): void {
    this.alertMessage.set(undefined);
    this.employeePendingDeletion.set(employee);

    const data: ConfirmationDialogData = {
      title: `Mitarbeiter löschen`,
      content: this.deleteConfirmationContentRef(),
      confirm: 'Löschen',
      decline: 'Abbrechen',
    };

    this.dialogService.open<ConfirmationDialogComponent, ConfirmationDialogData, boolean>(
      ConfirmationDialogComponent,
      { data },
    ).afterClosed().pipe(
      finalize((): void => {
        this.employeePendingDeletion.set(undefined);
      }),
      filter((confirmed: boolean | undefined): confirmed is true => confirmed === true),
      switchMap(() => this.employeeService.deleteEmployee(employee.id)),
    )
      .subscribe({
        next: () => {
          this.loadEmployees();
          this.alertMessage.set(this.alertMessages['deleteSuccess']);
        },
        error: () => {
          this.alertMessage.set(this.alertMessages['deleteError']);
        },
      });
  }

  public deleteConfirmationEmployeeEmail(): string | undefined {
    return this.employeePendingDeletion()?.email;
  }

  private loadEmployees(): void {
    this.employeeService.getEmployees().subscribe((employees: EmployeeModel[] | undefined) => {
      this.employees.set(employees);
    });
  }

  private loadDelegatableRoles(): void {
    this.employeeService.getDelegatableRoles().subscribe((delegatableRoles: EmployeeRoleModel[] | undefined) => {
      this.delegatableRoles.set(delegatableRoles);
    });
  }

  private mapSelectionListValueToRoles(value: SelectionListValue): EmployeeRoleRefModel[] {
    return typeof value === 'object' && value !== null
      ? Object.entries(value)
        .filter(([, checked]) => checked)
        .map(([id]) => ({ id: Number(id) }))
      : [];
  }

  private resolveInviteErrorMessage(error: unknown): AlertModel {
    const inviteError: EmployeeInviteValidationError | undefined = this.getInviteValidationError(error);

    if (inviteError?.code === 'EMPLOYEE_ALREADY_PERSISTED') {
      return this.alertMessages['inviteAlreadyExists'];
    }

    if (inviteError?.code === 'EMPLOYEE_OWN_EMAIL') {
      return this.alertMessages['inviteOwnEmail'];
    }

    return this.alertMessages['inviteError'];
  }

  private getInviteValidationError(error: unknown): EmployeeInviteValidationError | undefined {
    const response: EmployeeInviteErrorContainer = error as EmployeeInviteErrorContainer;

    return response.data?.exception?.errors?.[0];
  }
}
